import React, {useEffect, useState, useContext, useMemo} from 'react';
import {useNavigate} from 'react-router';
import {Switch} from '@blueprintjs/core';
import styled from 'styled-components';
import {SchemaForm} from "/src/components/forms";
import {cameraServers, locations, organisations} from "/src/services/api";
import {errorToast} from "/src/utils/toaster";
import {DataPageContext} from "/src/pages/data/context";


const AdvancedToggle = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 0.5em 0.5em 0;

    [class*="bp"][class*="-switch"] {
        margin-bottom: 0;
    }
`;


const _DevSchema = ({...props}) => ({
    type: "object",
    required: [
        "id",
        "name",
        "organisation"
    ],
    properties: {
        id: {
            type: "string",
            pattern: "^[a-f\\d]{23,26}$",
            readOnly: true
        },
        name: {
            type: "string"
        },
        createdAt: {
            type: "string",
            readOnly: true
        },
        updatedAt: {
            type: "string",
            readOnly: true
        },

        organisation: {
            oneOf: props.availableOrganisations && props.availableOrganisations.length ?
                props.availableOrganisations.map(org => ({const: org.id, title: org.name}))
                : [{const: ""}]
        },
        location: {
            oneOf: props.availableLocations && props.availableLocations.length ?
                props.availableLocations.map(loc => ({const: loc.id, title: loc.name}))
                : [{const: ""}]
        },

        status: {
            type: "string",
            enum: [
                'av received',
                'provisioned',
                'assigned',
                'dispatched',
                'client received',
                'connection confirmed',
                'decommissioned',
            ],
        },

        hardwareClass: {
            type: "string",
            enum: ['cbu', 'apu', 'nuc'],
        },
        releaseChannel: {
            type: "string",
            enum: ['edge', 'beta', 'candidate', 'stable'],
        },
        hardwareModel: {
            type: "string",
        },

        configuration: {
            type: "object",
            properties: {
                ML_Model: {
                    type: "object",
                    properties: {
                        detection_weights_path: {type: "string"},
                        training_config_path: {type: "string"},
                    }
                },
            }
        }
    }
})


const _UIDevSchema = {
    type: "VerticalLayout",
    elements: [
        {
            type: "Group",
            label: "Information",
            elements: [
                {
                    type: "HorizontalLayout",
                    elements: [
                        {type: "Control", scope: "#/properties/name", options: {disabled: true}},
                        {type: "Control", scope: "#/properties/id", options: {disabled: true}},
                        {type: "Control", scope: "#/properties/createdAt", options: {disabled: true}},
                        {type: "Control", scope: "#/properties/updatedAt", options: {disabled: true}},
                    ]
                },
            ]
        },
        {
            type: "Group",
            label: "Options",
            elements: [
                {
                    type: "HorizontalLayout",
                    elements: [
                        {type: "Control", scope: "#/properties/organisation", options: {fill: true}},
                        {type: "Control", scope: "#/properties/location", options: {fill: true}},
                        {type: "Control", scope: "#/properties/status", options: {fill: true}},
                        {type: "Control", scope: "#/properties/hardwareClass", options: {fill: true}},
                        {type: "Control", scope: "#/properties/releaseChannel", options: {fill: true}},
                        {type: "Control", scope: "#/properties/hardwareModel"},
                    ]
                },
            ]
        },
    ]
}

const _UIConfigSimple = {
    type: "Group",
    label: "Configuration",
    options: {collapsed: true},
    elements: [
        {type: "Control", label: "Detection Weights Path", scope: "#/properties/configuration/properties/ML_Model/properties/detection_weights_path"},
        {type: "Control", label: "Training Config Path", scope: "#/properties/configuration/properties/ML_Model/properties/training_config_path"},
    ]
}

const _UIConfigAdvanced = {
    type: "Group",
    elements: [
        {
            type: "Control",
            label: "Configuration",
            scope: "#/properties/configuration",
            options: {nested: false, rows: 25}
        }
    ]
}


const OneDev = ({devid}) => {
    const [info, setInfo] = useState(null);
    const [editing, setEditing] = useState(false);
    const [editErrors, setEditErrors] = useState(false);
    const [advanced, setAdvanced] = useState(false);
    const [options, setOptions] = useState({});
    const navigate = useNavigate();

    const {setNav, setControls, setLoading} = useContext(DataPageContext);

    const schema = useMemo(() => _DevSchema(options), [options]);

    useEffect(() => {
        setControls([
            {name: "Control", onClick: () => navigate(`/devs/${devid}/control`), icon: "dashboard"},
            {name: editing ? "Save" : "Edit", onClick: () => setEditing(prev => !prev), icon: editing ? "floppy-disk" : "edit", confirm: editing}
        ]);
        if (!editing && info) {
            if (editErrors) {
                errorToast({
                    message: info?.location
                    && options.availableOrganisations?.length
                    && !options.availableLocations?.map(loc => loc.id).includes(info.location)
                        ? "Location does not belong to the same organisation"
                        : "Some fields are wrong",
                    timeout: 5000
                });
                setEditing(true); // keep editing because of errors
            } else {
                setLoading({status: true, location: "Saving configuration..."});
                cameraServers.update(devid, {
                    organisation: info.organisation,
                    location: info.location,
                    hardwareClass: info.hardwareClass,
                    hardwareModel: info.hardwareModel,
                    releaseChannel: info.releaseChannel,
                    status: info.status,
                    configuration: info.configuration,
                }).then(() => {
                    // too many things change so better to reload - this will reset loading status and editing state
                    window.location.reload();
                }).catch((err) => {
                    console.log(err);
                    errorToast({message: "Failed to save " + err.message, timeout: 5000});
                    setEditing(true); // keep editing because of errors
                })
            }
        }
        return () => setControls([]);
    }, [editing])

    useEffect(() => {
        setLoading({status: true, location: `Loading device ${devid}`});
        cameraServers.get(devid, {include: 'organisationDetails,locationDetails,recordings'}).then(async apu => {
            if (!apu.configuration) apu.configuration = {};

            setLoading({status: true, location: `Loading options`});
            const availableOrganisations = await organisations.list();
            const availableLocations = apu.organisation ? await locations.list({withinOrganisations: apu.organisation._id}) : []

            if (!!apu.location?._id
                && !!availableOrganisations.length
                && !availableLocations.map(loc => loc.id).includes(apu.location._id)) {
                window.confirm("Device's location does not belong to the same organisation");
            }
            setOptions({
                availableOrganisations: availableOrganisations.sort((a, b) => a.name.localeCompare(b.name)),
                availableLocations: availableLocations.sort((a, b) => a.name.localeCompare(b.name)),
            });
            setInfo({...apu, organisation: apu.organisation?._id, location: apu.location?._id});
            setNav({
                left: [
                    ...apu.organisation?.name ? [{
                        name: apu.organisation.name,
                        path: "/orgs/" + apu.organisation._id,
                        icon: "office",
                        subpaths: [
                            {name: "Locations", icon: "area-of-interest", path: '/locs?org=' + apu.organisation._id},
                            {name: "Devices", icon: "desktop", path: '/devs?org=' + apu.organisation._id},
                            {name: "Cameras", icon: "camera", path: '/recs?org=' + apu.organisation._id},
                            {name: "Users", icon: "people", path: '/orgs/' + apu.organisation._id + '/users'},
                        ]
                    }] : [],
                    ...apu.location?.name ? [{
                        name: apu.location.name,
                        path: "/locs/" + apu.location._id,
                        icon: "area-of-interest",
                        subpaths: [
                            {name: "Devices", icon: "desktop", path: '/devs?loc=' + apu.location._id},
                            {name: "Cameras", icon: "camera", path: '/recs?loc=' + apu.location._id},
                        ]
                    }] : []
                ],
                right: [
                    {name: "Cameras", path: "/recs?dev=" + apu.id, icon: "camera"},
                ]
            });
            setLoading({status: false, location: `${apu.name} (${devid})`});
        }).catch(() => {
            setLoading({status: false, location: `Failed to load device ${devid}`});
        })
    }, []);

    const handleChange = ({data, errors}) => {
        setEditErrors(!!errors.length);
        setInfo(data);
        if (data.organisation && data.organisation !== info.organisation)
            locations.list({withinOrganisations: data.organisation}).then((locs) => {
                setOptions({...options, availableLocations: locs.sort((a, b) => a.name.localeCompare(b.name))});
            })
    };

    return !info ? null : (
        <>
            <SchemaForm
                schema={schema}
                uischema={_UIDevSchema}
                data={info}
                readonly={!editing}
                onChange={handleChange}
            />
            <AdvancedToggle>
                <Switch
                    label="Advanced"
                    checked={advanced}
                    onChange={() => setAdvanced(prev => !prev)}
                    disabled={!editing}
                />
            </AdvancedToggle>
            <SchemaForm
                schema={schema}
                uischema={advanced ? _UIConfigAdvanced : _UIConfigSimple}
                data={info}
                readonly={!editing}
                onChange={handleChange}
            />
        </>
    )
}

export {OneDev};
